import React, { useState } from 'react';

interface FrameSectionProps {
  onAddFrameElement: (frameStyle: string, frameColor: string, frameSize: number) => void;
  onSetActiveTool: (tool: string) => void;
}

const FrameSection: React.FC<FrameSectionProps> = ({ onAddFrameElement, onSetActiveTool }) => {
  const [selectedFrameStyle, setSelectedFrameStyle] = useState('square');
  const [selectedFrameColor, setSelectedFrameColor] = useState('#000000');
  const [selectedFrameSize, setSelectedFrameSize] = useState(4);

  const frameStyles = [
    {
      id: 'square',
      name: 'Square Frame',
      description: 'Classic rectangular border',
      preview: (
        <div className="w-full h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded border-4 border-white shadow-lg mb-2 flex items-center justify-center">
          <div className="text-white text-xs font-medium">Square Frame</div>
        </div>
      )
    },
    {
      id: 'circle',
      name: 'Circle Frame',
      description: 'Perfect circular border',
      preview: (
        <div className="w-full h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full border-4 border-white shadow-lg mb-2 flex items-center justify-center">
          <div className="text-white text-xs font-medium">Circle Frame</div>
        </div>
      )
    },
    {
      id: 'diamond',
      name: 'Diamond Frame',
      description: 'Elegant diamond shape',
      preview: (
        <div className="w-full h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded border-4 border-white shadow-lg mb-2 flex items-center justify-center transform rotate-45">
          <div className="text-white text-xs font-medium transform -rotate-45">Diamond Frame</div>
        </div>
      )
    },
    {
      id: 'rounded',
      name: 'Rounded Frame',
      description: 'Soft rounded corners',
      preview: (
        <div className="w-full h-20 bg-gradient-to-br from-pink-400 to-red-500 rounded-xl border-4 border-white shadow-lg mb-2 flex items-center justify-center">
          <div className="text-white text-xs font-medium">Rounded Frame</div>
        </div>
      )
    }
  ];

  const frameColors = [
    { color: '#ffffff', name: 'White' },
    { color: '#000000', name: 'Black' },
    { color: '#ef4444', name: 'Red' },
    { color: '#3b82f6', name: 'Blue' },
    { color: '#10b981', name: 'Green' },
    { color: '#f59e0b', name: 'Yellow' },
    { color: '#8b5cf6', name: 'Purple' },
    { color: '#f97316', name: 'Orange' },
    { color: '#ec4899', name: 'Pink' },
    { color: '#06b6d4', name: 'Cyan' },
    { color: '#84cc16', name: 'Lime' },
    { color: '#6b7280', name: 'Gray' }
  ];

  const frameSizes = [2, 4, 8];

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Frames</h3>
      
      <div className="space-y-6">
        {/* Frame Categories */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">Frame Styles</h4>
          <div className="grid grid-cols-2 gap-4">
            {frameStyles.map((style) => (
              <div 
                key={style.id}
                className={`bg-white border rounded-lg p-3 cursor-pointer transition-all ${
                  selectedFrameStyle === style.id 
                    ? 'shadow-md' 
                    : 'border-gray-300 hover:shadow-md'
                }`}
                style={selectedFrameStyle === style.id ? { borderColor: '#68aeb0' } : {}}
                onMouseEnter={(e) => {
                  if (selectedFrameStyle !== style.id) {
                    e.currentTarget.style.borderColor = '#68aeb0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedFrameStyle !== style.id) {
                    e.currentTarget.style.borderColor = '';
                  }
                }}
                onClick={() => setSelectedFrameStyle(style.id)}
              >
                {style.preview}
                <div className="text-sm font-medium text-gray-800 mb-1">{style.name}</div>
                <div className="text-xs text-gray-500">{style.description}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Frame Colors */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">Frame Colors</h4>
          <div className="grid grid-cols-6 gap-3">
            {frameColors.map((colorOption) => (
              <div
                key={colorOption.color}
                className={`w-8 h-8 border-2 rounded cursor-pointer transition-all ${
                  selectedFrameColor === colorOption.color 
                    ? 'scale-110 shadow-lg' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  backgroundColor: colorOption.color,
                  ...(selectedFrameColor === colorOption.color ? { borderColor: '#68aeb0' } : {})
                }}
                onClick={() => setSelectedFrameColor(colorOption.color)}
                title={colorOption.name}
              />
            ))}
          </div>
        </div>
        
        {/* Frame Sizes */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">Frame Sizes</h4>
          <div className="grid grid-cols-3 gap-3">
            {frameSizes.map((size) => (
              <div
                key={size}
                className={`h-12 bg-white border rounded cursor-pointer transition-colors flex items-center justify-center ${
                  selectedFrameSize === size 
                    ? '' 
                    : 'border-gray-300'
                }`}
                style={selectedFrameSize === size ? { borderColor: '#68aeb0' } : {}}
                onMouseEnter={(e) => {
                  if (selectedFrameSize !== size) {
                    e.currentTarget.style.borderColor = '#68aeb0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedFrameSize !== size) {
                    e.currentTarget.style.borderColor = '';
                  }
                }}
                onClick={() => setSelectedFrameSize(size)}
              >
                <div 
                  className="w-8 h-8 border rounded"
                  style={{ borderWidth: `${size}px`, borderColor: '#9ca3af' }}
                ></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Add Frame Button */}
        <div>
          <button
            onClick={() => {
              onAddFrameElement(selectedFrameStyle, selectedFrameColor, selectedFrameSize);
              onSetActiveTool('select');
            }}
            className="w-full px-4 py-3 text-white rounded-lg transition-colors flex items-center justify-center"
            style={{ backgroundColor: '#68aeb0' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Add Frame
          </button>
        </div>
      </div>
    </div>
  );
};

export default FrameSection; 